<template>
  <div v-if="isRemediationDateViewer">
    <confirm-dialog ref="confirmExport"></confirm-dialog>
    <div v-if="!isBusy && errorMessage" class="text-center alert alert-danger" role="alert">
      There was a problem with fetching Remediation Date History {{ errorMessage }}
    </div>
    <div v-if="!isBusy && model && model.length === 0" class="text-center alert alert-warning " role="alert">
      No Remediation Date History found.
    </div>
    <table v-if="model && model.length > 0" class="table table-hover">
      <thead>
        <tr>
          <th scope="col">Date of action</th>
          <th scope="col">TM username who ran it</th>
          <th scope="col">TMLN</th>
          <th scope="col">Business Name</th>
          <th scope="col">8 Week Remediation Date</th>
          <th scope="col">TrustMark Revised Deadline (Old)</th>
          <th scope="col">TrustMark Revised Deadline (New)</th>
          <th scope="col">Number of projects updated</th>
          <th scope="col">Uploaded files</th>
          <th scope="col">Export Measures</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="remediationDateHistory in model"
          :key="remediationDateHistory.remediationDateHistoryId"
        >
          <!-- Main row -->
          <tr>
            <td>{{ $helpers.dateString(remediationDateHistory.dateOfAction) }}</td>
            <td>{{ remediationDateHistory.trustMarkUsername }}</td>
            <td>{{ remediationDateHistory.tmln }}</td>
            <td>{{ remediationDateHistory.businessName }}</td>
            <td>{{ $helpers.dateString(remediationDateHistory.eightWeekRemediationDate) }}</td>
            <td>{{ $helpers.dateString(remediationDateHistory.trustMarkRevisedDeadlineOld) }}</td>
            <td>{{ $helpers.dateString(remediationDateHistory.trustMarkRevisedDeadlineNew) }}</td>
            <td>{{ remediationDateHistory.numberOfProjectsUpdated }}</td>

            <!-- Expand button -->
            <td :class="{ 'no-border-bottom': isPanelExpanded[remediationDateHistory.remediationDateHistoryId] }" class="text-right">
              <button class="btn btn-primary" @click="toggleEdit(remediationDateHistory?.remediationDateHistoryId)">
                  <span v-if="isPanelExpanded[remediationDateHistory?.remediationDateHistoryId]">
                      <font-awesome-icon icon="chevron-down" />
                  </span>
                  <span v-else>
                      <font-awesome-icon icon="chevron-up" />
                  </span>
              </button>
            </td>
            <td @click="confirmExportRemediationDateHistoryMeasure(remediationDateHistory.remediationDateHistoryId)">
              <font-awesome-icon icon="file-export" size="2x" class="text-primary" style="cursor: pointer;" />
            </td>
          </tr>

          <!-- Expandable file upload row -->
          <tr v-if="isPanelExpanded[remediationDateHistory.remediationDateHistoryId]" class="bg-light">
            <td colspan="10" class="p-3">
              <card :label="`Uploaded files in support - ${remediationDateHistory.evidenceUploads.length}`">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th scope="col">Date of Upload</th>
                      <th scope="col">Uploaded By</th>
                      <th scope="col">Name</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="evidence in remediationDateHistory.evidenceUploads" :key="evidence.evidenceUploadId">
                      <td>{{ $helpers.dateString(evidence.uploadedAt) }}</td>
                      <td>{{ evidence.uploadedBy }}</td>
                      <td>{{ evidence.evidenceFilename }}</td>
                      <td>
                        <div class="btn-group btn-group">
                          <button type="button" class="btn btn-secondary" 
                            @click="viewFile(evidence.evidenceLink)">
                              View
                          </button>
                          <button type="button" class="btn btn-secondary"
                            @click="downloadFile(evidence.evidenceLink)">
                            Download
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="remediationDateHistory.evidenceUploads.length === 0">
                      <td colspan="4" class="text-center">No files are uploaded</td>
                    </tr>
                  </tbody>
                </table>
              </card>
            </td>
            </tr>
        </template>
      </tbody>
    </table>

    <div v-if="!isBusy && !model && !errorMessage" class="text-center alert alert-info" role="alert">
      Remediation Date History data will be listed here.
    </div>
  </div>
  <DocumentViewer ref="documentViewer" />
</template>

<script>
import { SmartButton, Card, ConfirmDialog, confirmType } from '@trustmark-2005-ltd/trustmark-shared-components';
import DocumentViewer from '../DocumentViewer.vue';
import RemediationDateHistoryEvidenceService from '../../services/remediation-date-history-evidence-service';

export default {
  name: "RemediationDateHistoryTable",
  components: {
    SmartButton,
    Card,
    ConfirmDialog,
    DocumentViewer,
  },
  data() {
    return {
      errorMessage: null,
      isPanelExpanded: {},
    };
  },
  computed: {
    model() {
      return this.$store.state.licenceNumber.remediationDateHistory;
    },
    tmlnModel() {
      return this.$store.state.licenceNumber.trustmarkBusiness.tmln;
    },
    currentAccount() {
      return this.$store.state.auth.account;
    },
    isRemediationDateViewer() {
      return this.currentAccount && this.currentAccount.roles
        ? this.currentAccount.roles.includes("RemediationDateViewer")
        : false;
    },
    isBusy() {
      return this.$store.state.isBusy;
    },
  },
  methods: {
    toggleEdit(remediationDateHistoryId) {
      this.isPanelExpanded[remediationDateHistoryId] = !this.isPanelExpanded[remediationDateHistoryId];
    },
    confirmExportRemediationDateHistoryMeasure(remediationDateHistoryId) {
      this.$refs.confirmExport
        .show({
          title: 'Confirm Export',
          message: 'Are you sure you want to export remediation date history?',
           okButtonText: 'Confirm Export',
          confirmType: confirmType.standard,
        })
        .then((result) => {
          if (result === true) {
            this.$store.dispatch("licenceNumber/requestRemediationDateHistoryMeasureExport", {remediationDateHistoryId})
              .then((result) => {
                const downloadUrl = window.URL.createObjectURL(new Blob([result]));
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', `export-${new Date().valueOf()}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              });
          }
        });
    },
    downloadFile(url) {
      RemediationDateHistoryEvidenceService.getRemediationDateHistoryDownloadUrl(url)
        .then((result) => {
          window.open(result, '_blank');
        })
        .catch(() => {
          this.customError = 'Could not download file, please try again.';
        });
    },
    viewFile(url) {
      RemediationDateHistoryEvidenceService.getRemediationDateHistoryDownloadUrl(url)
        .then((result) => {
          this.$refs.documentViewer.onView(result);
        })
        .catch(() => {
          this.customError = 'Could not view file, please try again.';
        });
    },
  },
  async mounted() {
    this.errorMessage = null;
    try {
      const paload = { tmln: this.tmlnModel };
      await this.$store.dispatch("licenceNumber/getRemediationDateHistoryByTmln", paload);
    } catch (error) {
      if (error) {
        this.errorMessage = error?.message;
      }
    }
  },
};
</script>
